import { useNavigate } from 'react-router-dom';
import { IconButton } from '@/components/atoms/IconButton';
import { Avatar } from '@/components/atoms/Avatar';
import { SearchPill } from '@/components/molecules/SearchPill';
import { Crumbs } from '@/components/molecules/Crumbs';
import { LocaleSwitcher } from '@/components/molecules/LocaleSwitcher';
import { usePaletteStore } from '@/stores/palette';
import { useThemeStore } from '@/stores/theme';
import { useRoleStore } from '@/stores/role';
import { useAuthStore } from '@/stores/auth';
import { ROLES } from '@/data/roles';
import { toast } from '@/stores/toast';
import { useNotificationsStore } from '@/stores/notifications';
import { useCartCount } from '@/stores/cart';
import { BETA_MODE } from '@/app/BetaRouteGate';

type Props = { crumb: string };

export const Topbar = ({ crumb }: Props) => {
  const openPalette = usePaletteStore((s) => s.show);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);
  const role = useRoleStore((s) => s.role);
  const user = useAuthStore((s) => s.user);
  const unread = useNotificationsStore((s) => s.unread);
  const markAllRead = useNotificationsStore((s) => s.markAllRead);
  const cartCount = useCartCount();
  const navigate = useNavigate();
  const identity = ROLES[user?.role ?? role].identity;
  const initials = user?.name
    ? user.name.split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase()
    : identity.avatar;

  return (
    <header className="topbar">
      <Crumbs here={crumb} />
      {!BETA_MODE && <SearchPill onOpen={openPalette} />}
      <LocaleSwitcher />
      <div className="top-icons">
        <IconButton
          icon="eye"
          label="Tema"
          onClick={() => {
            toggleTheme();
            toast('Tema alternado');
          }}
        />
        {!BETA_MODE && <button
          type="button"
          aria-label={`Notificações${unread > 0 ? ` · ${unread} não lidas` : ''}`}
          className="icon-btn"
          onClick={() => {
            markAllRead();
            navigate('/notifications');
          }}
          style={{ position: 'relative' }}
        >
          <svg width={19} height={19} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}>
            <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.7 21a2 2 0 0 1-3.4 0" />
          </svg>
          {unread > 0 && (
            <span
              style={{
                position: 'absolute',
                top: 4,
                right: 4,
                minWidth: 18,
                height: 18,
                padding: '0 4px',
                borderRadius: 9,
                background: 'var(--danger)',
                color: 'white',
                fontSize: 10,
                fontWeight: 700,
                fontFamily: 'var(--font-mono)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>}
        {!BETA_MODE && cartCount > 0 && (
          <button
            type="button"
            aria-label={`Carrinho · ${cartCount} itens`}
            className="icon-btn"
            onClick={() => navigate('/marketplace/cart')}
            style={{ position: 'relative' }}
          >
            <svg width={19} height={19} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}>
              <path d="M6 8h12l-1 12H7Z" />
              <path d="M9 8V6a3 3 0 0 1 6 0v2" />
            </svg>
            <span
              style={{
                position: 'absolute',
                top: 4,
                right: 4,
                minWidth: 18,
                height: 18,
                padding: '0 4px',
                borderRadius: 9,
                background: 'var(--primary)',
                color: 'var(--primary-ink)',
                fontSize: 10,
                fontWeight: 700,
                fontFamily: 'var(--font-mono)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {cartCount > 9 ? '9+' : cartCount}
            </span>
          </button>
        )}
        <button
          type="button"
          className="icon-btn profile-btn"
          aria-label="Minha conta"
          onClick={() => navigate('/account')}
        >
          <Avatar initials={initials} size={30} bg={identity.bg} color="var(--primary-ink)" />
        </button>
      </div>
    </header>
  );
};
